import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  Strategy,
  isRedlineStrategy,
  isHotLapStrategy,
  isSlipstreamStrategy,
  isApexStrategy,
  isPitlaneStrategy,
  isOvertakeStrategy,
  isPursuitStrategy,
  isChicaneStrategy,
  PreTradeIndicators,
  StrategyEvent,
  RedlineStrategyEvent,
  HotLapStrategyEvent,
  SlipstreamStrategyEvent,
  ApexStrategyEvent,
  PitlaneStrategyEvent,
  OvertakeStrategyEvent,
  PursuitStrategyEvent,
  ChicaneStrategyEvent
} from '../../types/strategy-entities';

import { initialState, StrategyState } from './state';

function overwriteLatestEvent(strategy: Strategy, latestEvent: StrategyEvent) {
  if (isRedlineStrategy(strategy)) {
    strategy.latestEvent = latestEvent as RedlineStrategyEvent;
  } else if (isHotLapStrategy(strategy)) {
    strategy.latestEvent = latestEvent as HotLapStrategyEvent;
  } else if (isSlipstreamStrategy(strategy)) {
    strategy.latestEvent = latestEvent as SlipstreamStrategyEvent;
  } else if (isApexStrategy(strategy)) {
    strategy.latestEvent = latestEvent as ApexStrategyEvent;
  } else if (isPitlaneStrategy(strategy)) {
    strategy.latestEvent = latestEvent as PitlaneStrategyEvent;
  } else if (isOvertakeStrategy(strategy)) {
    strategy.latestEvent = latestEvent as OvertakeStrategyEvent;
  } else if (isPursuitStrategy(strategy)) {
    strategy.latestEvent = latestEvent as PursuitStrategyEvent;
  } else if (isChicaneStrategy(strategy)) {
    strategy.latestEvent = latestEvent as ChicaneStrategyEvent;
  }
}

const strategySlice = createSlice({
  name: 'strategy',
  initialState,
  reducers: {
    reset: () => initialState,
    loading: (state: StrategyState, action: PayloadAction<boolean>) => {
      state.error = undefined;
      state.loading = action.payload;
      state.loaded = !action.payload;
      state.pending = false;
      state.preTradeIndicatorsPending = false;
    },
    pending: (state: StrategyState, action: PayloadAction<boolean>) => {
      state.error = undefined;
      state.pending = action.payload;
    },
    preTradeIndicatorsPending: (state: StrategyState, action: PayloadAction<boolean>) => {
      state.error = undefined;
      state.preTradeIndicatorsPending = action.payload;
    },
    success: (state: StrategyState, action: PayloadAction<Strategy[]>) => {
      state.error = undefined;
      state.loading = false;
      state.loaded = true;
      state.strategies = action.payload;
    },
    fail: (state: StrategyState, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
      state.loaded = true;
      state.strategies = [];
    },
    add: (state: StrategyState, action: PayloadAction<Strategy>) => {
      state.strategies.push(action.payload);
      state.loading = false;
      state.loaded = true;
    },
    update: (state: StrategyState, action: PayloadAction<Strategy>) => {
      const dbEntry = state.strategies.find((x) => x.id === action.payload.id);
      if (!!dbEntry) {
        const index = state.strategies.indexOf(dbEntry);
        state.strategies[index] = action.payload;
      }
      state.pending = false;
      state.preTradeIndicatorsPending = false;
    },
    updatePreTradeIndicators: (
      state: StrategyState,
      action: PayloadAction<{ strategyId: string; preTradeIndicators: PreTradeIndicators | null }>
    ) => {
      const entry = state.strategies.find((x) => x.id === action.payload.strategyId);
      if (entry && isRedlineStrategy(entry)) {
        entry.preTradeIndicators = action.payload.preTradeIndicators;
      }
      state.preTradeIndicatorsPending = false;
    },
    updateLatestEvents: (state: StrategyState, action: PayloadAction<StrategyEvent[]>) => {
      for (const latestEvent of action.payload) {
        const strategyId = latestEvent.strategyId;
        if (!strategyId) {
          continue;
        }
        const entry = state.strategies.find((x) => x.id === strategyId);
        if (!entry) {
          continue;
        }
        overwriteLatestEvent(entry, latestEvent);
      }
    },
    remove: (state: StrategyState, action: PayloadAction<Strategy>) => {
      const entry = state.strategies.find((x) => x.id === action.payload.id);
      const index = entry ? state.strategies.indexOf(entry) : -1;
      if (index !== -1) {
        state.strategies.splice(index, 1);
      }
      state.loading = false;
      state.loaded = true;
    }
  }
});

const { actions, reducer } = strategySlice;
export const {
  reset,
  loading,
  pending,
  preTradeIndicatorsPending,
  success,
  fail,
  add,
  update,
  updatePreTradeIndicators,
  updateLatestEvents,
  remove
} = actions;
export default reducer;
